import React, { FC, useEffect, useRef, useState } from 'react'
import { Dropdown, DropdownFilterEvent } from 'primereact/dropdown'
import { useGetSimTemplates } from '../../api/submission-API'
import { DropdownStyle } from '../../utils/styling-constants'
import { CFDFile } from '@aero-platform/shared'
import { fetchAllSimTemplates, fetchSimTemplatesByQuery } from './sssearch/SimTemplate'

const FILTER_DELAY_MS = 600

/**
 * Sentinel used when a search query returns no results.
 *
 * Sentinels are detected by `fileType` (not `file`), because PrimeReact's built-in
 * client-side filter (filterBy="file") would otherwise filter them out when the
 * user's query doesn't substring-match the sentinel's file. We set the sentinel's
 * `file` to the current query so the built-in filter always keeps it visible.
 */
const SENTINEL_LOADING = 'sentinel-loading'
const SENTINEL_NO_RESULTS = 'sentinel-no-results'

const makeLoadingSentinel = (query: string): CFDFile =>
  ({
    system: '— Loading —',
    file: query,
    fileType: SENTINEL_LOADING
  }) as CFDFile

const makeNoResultsSentinel = (query: string): CFDFile =>
  ({
    system: '— No results —',
    file: query,
    fileType: SENTINEL_NO_RESULTS
  }) as CFDFile

const isSentinel = (opt?: CFDFile | null) =>
  opt?.fileType === SENTINEL_LOADING || opt?.fileType === SENTINEL_NO_RESULTS

type Props = {
  submissionId?: string
  submissionName?: string
  methodologyTemplates?: CFDFile[]
  /** Extra selectable templates appended to the list (never auto-selected or used as a default). */
  extraOptions?: CFDFile[]
  value?: CFDFile | null
  onChange?: (value: CFDFile | null) => void
  placeholder?: string
  autoSelectFirstOption?: boolean
  disabled?: boolean
  className?: string
}

const SimTemplateDropdown: FC<Props> = ({
  submissionId,
  submissionName,
  methodologyTemplates,
  extraOptions,
  value,
  onChange,
  placeholder,
  autoSelectFirstOption = true,
  disabled,
  className
}) => {
  const [{}, doFetch] = useGetSimTemplates()
  const [options, setOptions] = useState<CFDFile[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadOptions = async (query: string) => {
    if (!submissionId) return
    try {
      setIsLoading(true)
      const opts = query
        ? await fetchSimTemplatesByQuery(doFetch, submissionId, submissionName || '', query)
        : await fetchAllSimTemplates(doFetch, submissionId, submissionName || '')
      if (value && !query) {
        const matchingOpt = opts.find(
          o =>
            o.file === value.file && o.fileType === value.fileType && o.rootType === value.rootType
        )
        if (matchingOpt) {
          const needsEnrichment =
            (!value.path || !value.path.startsWith('https://')) &&
            matchingOpt.path?.startsWith('https://')
          if (needsEnrichment) {
            // Current value is missing or has a relative path — silently enrich it from the loaded option
            onChange?.(matchingOpt)
          }
        } else {
          // Value not in list — prepend so it remains selectable/visible
          opts.unshift(value)
        }
      }
      opts.forEach(o => {
        o.description = `(${o.rootType}-${o.fileType || o.rootType || o.system})-${o.file}`
      })
      // When a query is active and the server returns nothing, use a disabled sentinel
      // instead of an empty array. An empty options array triggers PrimeReact's internal
      // filter-reset guard (useUpdateEffect: if filterState && !options.length → clear),
      // which would wipe the user's typed text. The sentinel keeps the array non-empty
      // while still showing a "no results" row to the user.
      setOptions(opts.length === 0 && query ? [makeNoResultsSentinel(query)] : opts)
      setIsLoading(false)
    } catch {
      setIsLoading(false)
    }
  }

  // Initial fetch when the submission is known
  useEffect(() => {
    if (!submissionId) {
      // For new runs without a submission ID, use methodology templates as fallback
      if (methodologyTemplates && methodologyTemplates.length > 0) {
        const opts = methodologyTemplates.map(t => ({
          ...t,
          description: `(${t.rootType}-${t.fileType || t.rootType || t.system})-${t.file}`
        })) as CFDFile[]
        setOptions(opts)
      }
      return
    }
    loadOptions('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId, submissionName, methodologyTemplates])

  // Auto-select the first option when no value is set and options have loaded.
  useEffect(() => {
    if (autoSelectFirstOption && !value && options.length > 0 && !isSentinel(options[0])) {
      onChange?.(options[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, autoSelectFirstOption])

  const handleFilter = (e: DropdownFilterEvent) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const query = e.filter || ''
    setIsLoading(true)
    setOptions([makeLoadingSentinel(query)])
    debounceRef.current = setTimeout(() => loadOptions(query), FILTER_DELAY_MS)
  }

  const handleChange = (e: { value: CFDFile | null }) => {
    const opt = e.value as CFDFile | null
    if (isSentinel(opt)) return // sentinels not selectable
    onChange?.(opt ?? null)
  }

  // Prefer the exact option object from the loaded list so PrimeReact can find it
  // via dataKey equality. Fall back to the raw value so the label still renders
  // before the list has loaded (edit-mode persistence) or after a filtered search
  // clears results that included the selected item.
  //
  // Append inherited/extra templates that aren't already present so they remain selectable.
  const mergedOptions = React.useMemo(() => {
    if (!extraOptions?.length) return options
    const withDesc = extraOptions.map(t => ({
      ...t,
      description:
        t.description || `(${t.rootType}-${t.fileType || t.rootType || t.system})-${t.file}`
    })) as CFDFile[]
    const extras = withDesc.filter(
      e =>
        !options.some(
          o => o.file === e.file && o.fileType === e.fileType && o.rootType === e.rootType
        )
    )
    return [...options, ...extras]
  }, [options, extraOptions])

  const displayValue = value?.file
    ? mergedOptions.find(
        o => o.file === value.file && o.fileType === value.fileType && o.rootType === value.rootType
      ) ?? value
    : null

  /**
   * PrimeReact v10 passes the matched option from the `options` array to valueTemplate,
   * or null when the value is not found in the current options list.
   * Fall back to `displayValue` so the label still renders in both cases.
   */
  const valueTemplate = (option: CFDFile | null) => {
    const opt = option ?? displayValue
    if (!opt?.file)
      return <span className="p-dropdown-placeholder">{placeholder || 'Select template'}</span>
    return <span>{opt.file}</span>
  }

  const itemTemplate = (option: CFDFile) => {
    if (option?.fileType === SENTINEL_LOADING) {
      return (
        <div className="p-dropdown-item p-disabled" style={{ fontStyle: 'italic', opacity: 0.6 }}>
          Loading...
        </div>
      )
    }
    if (option?.fileType === SENTINEL_NO_RESULTS) {
      return (
        <div className="p-dropdown-item p-disabled" style={{ fontStyle: 'italic', opacity: 0.6 }}>
          No results found
        </div>
      )
    }
    const cls = DropdownStyle?.item?.className || ''
    return <div className={cls}>{option?.file}</div>
  }

  // Note for dataKey: Use `description` (e.g. "(rootType-fileType) file") as the unique identifier for selection. This is a case where the master geometry's template may have the same name as the candidate/template files
  return (
    <Dropdown
      value={displayValue}
      dataKey="description"
      pt={DropdownStyle}
      options={mergedOptions}
      optionLabel="file"
      optionDisabled={(opt: CFDFile) => isSentinel(opt)}
      filter
      filterBy="file"
      loading={isLoading}
      onFilter={handleFilter}
      valueTemplate={valueTemplate}
      itemTemplate={itemTemplate}
      onChange={handleChange}
      placeholder={placeholder || 'Select template'}
      disabled={disabled}
      className={className}
      emptyMessage="&nbsp;&nbsp;No Data"
    />
  )
}

export default SimTemplateDropdown
